// /combos/:id — Combo Detail (PAGES.md §3.97)
//
// View/edit existing combo. Delete: OWNER+ (50). Edit: MANAGER+ (30).
// RESTAURANT only. Traceability: FR-ORD-047, FR-ORD-048

import { getSubunitMultiplier } from '@bbpos/utils';
import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  PageContainer,
  Button,
  Input,
  Toggle,
  TimePicker,
  DatePicker,
  Skeleton,
  Alert,
  ErrorBoundary,
  Modal,
  ConfirmDialog,
  StatCard,
  FormField,
  FileUpload,
  DataTable,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import { comboService, DAY_LABELS, ROLE_LEVELS, comboDateToApi, comboDateToPicker } from '@/services/combo.service';
import type { SelectedItem, ProductMinimal } from '@/services/combo.service';
import { productService } from '@/services/product.service';
import { useAuthStore } from '@/stores/use-auth-store';
import { useDashboardStore } from '@/stores/use-dashboard-store';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';
import { GLASS, GLASS_FORM, cn } from '@/constants/design-tokens';
import { formatPaise, formatPaiseCompact, formatNumber } from '@/utils/currency';
import { ApiError } from '@/lib/api-client';
import { PlusIcon, TrashIcon, PackageIcon, ImageIcon } from './icons';

// ─── Component ──────────────────────────────────────────────────────────────

function ComboDetailContent() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const selectedLocationId = useDashboardStore((s) => s.selectedLocationId);
  const role = useAuthStore((s) => s.profile?.role ?? '');
  const canDelete = (ROLE_LEVELS[role] ?? 0) >= ROLE_LEVELS.OWNER;

  const [isLoading, setIsLoading] = useState(true);
  const [name, setName] = useState('');
  const [priceDisplay, setPriceDisplay] = useState('');
  const [items, setItems] = useState<SelectedItem[]>([]);
  const [isActive, setIsActive] = useState(true);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [alwaysAvailable, setAlwaysAvailable] = useState(true);
  const [availStart, setAvailStart] = useState('12:00');
  const [availEnd, setAvailEnd] = useState('15:00');
  const [availDays, setAvailDays] = useState<number[]>([1, 2, 3, 4, 5]);
  const [validFrom, setValidFrom] = useState<Date | undefined>(undefined);
  const [validUntil, setValidUntil] = useState<Date | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [totalSold, setTotalSold] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [avgOrdersPerDay, setAvgOrdersPerDay] = useState(0);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [products, setProducts] = useState<ProductMinimal[]>([]);
  const [productSearch, setProductSearch] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const comboPricePaise = Math.round(parseFloat(priceDisplay || '0') * getSubunitMultiplier(currency));
  const individualTotal = useMemo(() => items.reduce((s, i) => s + i.quantity * i.individual_price, 0), [items]);
  const savingsPercent = individualTotal > 0
    ? Math.round(((individualTotal - comboPricePaise) / individualTotal) * 10000) / 100
    : 0;
  const priceExceedsTotal = comboPricePaise > 0 && individualTotal > 0 && comboPricePaise >= individualTotal;

  const fetchCombo = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const combo = await comboService.getById(id);
      setName(combo.name);
      setPriceDisplay(String(combo.combo_price / getSubunitMultiplier(currency)));
      setItems(combo.items.map((i) => ({ ...i })));
      setIsActive(combo.is_active);
      setImagePreview(combo.image_url);
      setTotalSold(combo.total_sold ?? 0);
      setTotalRevenue(combo.total_revenue ?? 0);
      setAvgOrdersPerDay(combo.avg_orders_per_day ?? 0);
      if (combo.availability_start || combo.availability_end) {
        setAlwaysAvailable(false);
        setAvailStart(combo.availability_start ?? '00:00');
        setAvailEnd(combo.availability_end ?? '23:59');
        setAvailDays(combo.availability_days ?? [0, 1, 2, 3, 4, 5, 6]);
      } else {
        setAlwaysAvailable(true);
      }
      setValidFrom(comboDateToPicker(combo.valid_from));
      setValidUntil(comboDateToPicker(combo.valid_until));
    } catch (err) {
      if (err instanceof ApiError && err.statusCode === 404) {
        toast({ title: 'Combo not found', variant: 'error' });
        navigate('/combos', { replace: true });
        return;
      }
      toast({ title: err instanceof ApiError ? err.message : 'Failed to load combo.', variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [id, navigate, toast]);

  useEffect(() => { fetchCombo(); }, [fetchCombo]);

  const fetchProducts = useCallback(async (query?: string) => {
    try {
      const result = await productService.list({ location_id: selectedLocationId ?? undefined, search: query || undefined, limit: 200 });
      setProducts((result.data as unknown as ProductMinimal[]).map((p) => ({ id: p.id, name: p.name, price: (p as unknown as { price: number }).price ?? 0 })));
    } catch { /* silent */ }
  }, [selectedLocationId]);

  const openPicker = useCallback(() => {
    setProductSearch('');
    setPickerOpen(true);
    fetchProducts();
  }, [fetchProducts]);

  const handleProductSearch = useCallback((val: string) => {
    setProductSearch(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchProducts(val), 300);
  }, [fetchProducts]);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const addProduct = useCallback((p: ProductMinimal) => {
    if (items.some((i) => i.product_id === p.id)) return;
    setItems((prev) => [...prev, { id: `sel-${Date.now()}`, product_id: p.id, product_name: p.name, quantity: 1, individual_price: p.price }]);
    setPickerOpen(false);
    setFormError(null);
  }, [items]);

  const removeItem = useCallback((productId: string) => { setItems((prev) => prev.filter((i) => i.product_id !== productId)); }, []);
  const updateQuantity = useCallback((productId: string, qty: number) => {
    setItems((prev) => prev.map((i) => (i.product_id === productId ? { ...i, quantity: Math.max(1, qty) } : i)));
  }, []);

  const toggleDay = useCallback((day: number) => {
    setAvailDays((prev) => prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day].sort());
  }, []);

  const handleImageUpload = useCallback((files: File[]) => {
    if (files.length === 0) { setImagePreview(null); return; }
    const url = URL.createObjectURL(files[0]);
    setImagePreview(url);
  }, []);

  const validate = useCallback((): boolean => {
    if (!name.trim()) { setFormError('Combo name is required'); return false; }
    if (items.length < 2) { setFormError('Add at least 2 items'); return false; }
    if (comboPricePaise <= 0) { setFormError('Combo price must be greater than zero'); return false; }
    if (priceExceedsTotal) { setFormError(`Combo price must be less than individual total (${formatPaise(individualTotal, currency)})`); return false; }
    if (validFrom && validUntil && validUntil < validFrom) { setFormError('Valid Until must be on or after Valid From'); return false; }
    setFormError(null);
    return true;
  }, [name, items, comboPricePaise, priceExceedsTotal, individualTotal, currency, validFrom, validUntil]);

  const handleSave = useCallback(async () => {
    if (!validate() || !id) return;
    setIsSaving(true);
    try {
      await comboService.update(id, {
        name: name.trim(),
        combo_price: comboPricePaise,
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        is_active: isActive,
        availability_start: alwaysAvailable ? undefined : availStart,
        availability_end: alwaysAvailable ? undefined : availEnd,
        availability_days: alwaysAvailable ? undefined : availDays,
        // Send null (not undefined) so clearing a date actually unsets it.
        valid_from: comboDateToApi(validFrom) ?? null,
        valid_until: comboDateToApi(validUntil) ?? null,
      });
      toast({ title: 'Combo saved', variant: 'success' });
      navigate('/combos');
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.message.includes('COMBO_NAME_DUPLICATE')) setFormError('A combo with this name already exists');
        else toast({ title: err.message, variant: 'error' });
      } else {
        toast({ title: 'Failed to save', variant: 'error' });
      }
    } finally {
      setIsSaving(false);
    }
  }, [validate, id, name, comboPricePaise, items, isActive, alwaysAvailable, availStart, availEnd, availDays, validFrom, validUntil, toast]);

  const handleDelete = useCallback(async () => {
    if (!id) return;
    setIsDeleting(true);
    try {
      await comboService.deactivate(id);
      toast({ title: 'Combo deleted', variant: 'success' });
      navigate('/combos', { replace: true });
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to delete.', variant: 'error' });
    } finally {
      setIsDeleting(false);
      setDeleteConfirmOpen(false);
    }
  }, [id, navigate, toast]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') { e.preventDefault(); handleSave(); }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleSave]);

  const filteredProducts = useMemo(() => {
    const q = productSearch.toLowerCase();
    return products.filter((p) => !items.some((i) => i.product_id === p.id) && p.name.toLowerCase().includes(q));
  }, [products, items, productSearch]);

  const itemColumns: ColumnDef<SelectedItem>[] = useMemo(() => [
    { key: 'product_name', header: 'Item', render: (_v: unknown, r: SelectedItem) => <span className="font-semibold text-neutral-800 dark:text-neutral-200">{r.product_name}</span> },
    { key: 'quantity', header: 'Qty', numeric: true, render: (_v: unknown, r: SelectedItem) => (
      <input type="number" value={r.quantity} onChange={(e) => updateQuantity(r.product_id, Number(e.target.value) || 1)} min="1"
        className="w-16 px-2 py-1 rounded-lg bg-neutral-50 dark:bg-neutral-700 text-sm font-mono tabular-nums text-neutral-900 dark:text-neutral-100 focus:ring-2 focus:ring-violet-500 focus:outline-none" aria-label={`Quantity for ${r.product_name}`} />
    )},
    { key: 'individual_price', header: 'Unit Price', numeric: true, render: (_v: unknown, r: SelectedItem) => <span className="font-mono tabular-nums text-neutral-600 dark:text-neutral-400">{formatPaise(r.individual_price, currency)}</span> },
    { key: 'line_total', header: 'Subtotal', numeric: true, render: (_v: unknown, r: SelectedItem) => <span className="font-mono tabular-nums font-bold">{formatPaise(r.quantity * r.individual_price, currency)}</span> },
    { key: 'actions', header: '', render: (_v: unknown, r: SelectedItem) => (
      <Button variant="ghost" size="sm" onClick={() => removeItem(r.product_id)} className="text-danger-600 hover:text-danger-700 hover:bg-danger-50 dark:hover:bg-danger-900/20" aria-label={`Remove ${r.product_name}`}><TrashIcon /></Button>
    )},
  ], [updateQuantity, removeItem, currency]);

  if (isLoading) {
    return (
      <PageContainer title="Loading..." breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Combos', href: '/combos' }, { label: '...' }]}>
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => <div key={i} className={cn(GLASS, 'p-4')}><Skeleton variant="text" width="50%" height={14} className="mb-2" /><Skeleton variant="text" width="30%" height={24} /></div>)}
          </div>
          <div className={cn(GLASS_FORM, 'p-5 space-y-4')}><Skeleton variant="text" width="30%" height={20} /><Skeleton variant="rect" width="100%" height={40} /><Skeleton variant="rect" width="100%" height={40} /></div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer title={name || 'Combo Detail'}
      breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Combos', href: '/combos' }, { label: name || 'Detail' }]}
      actions={
        <div className="flex items-center gap-3">
          {canDelete && (
            <Button variant="ghost" size="sm" onClick={() => setDeleteConfirmOpen(true)} className="text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20" aria-label="Delete combo">
              <TrashIcon /><span className="ml-1.5 hidden sm:inline">Delete</span>
            </Button>
          )}
          <Button variant="primary" size="sm" onClick={handleSave} disabled={isSaving}>{isSaving ? 'Saving...' : 'Save Combo'}</Button>
        </div>
      }
    >
      <div className="animate-fade-in space-y-6">
        {/* Pricing Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="animate-slide-up">
            <StatCard title="Individual Total" value={formatPaise(individualTotal, currency)} icon={<PackageIcon className="h-4 w-4" />} />
          </div>
          <div className="animate-slide-up" style={{ animationDelay: '50ms' }}>
            <StatCard title="Combo Price" value={comboPricePaise > 0 ? formatPaise(comboPricePaise, currency) : '—'} icon={<PackageIcon className="h-4 w-4" />} />
          </div>
          <div className="animate-slide-up" style={{ animationDelay: '100ms' }}>
            <StatCard title="Savings" value={savingsPercent > 0 ? `${savingsPercent.toFixed(1)}%` : '—'} icon={<PackageIcon className="h-4 w-4" />} />
          </div>
          {/* Sales Metrics (§3.97) */}
          <div className="animate-slide-up" style={{ animationDelay: '150ms' }}>
            <StatCard title="Total Sold" value={formatNumber(totalSold)} icon={<PackageIcon className="h-4 w-4" />} />
          </div>
          <div className="animate-slide-up" style={{ animationDelay: '200ms' }}>
            <StatCard title="Revenue" value={formatPaiseCompact(totalRevenue, currency)} icon={<PackageIcon className="h-4 w-4" />} />
          </div>
          <div className="animate-slide-up" style={{ animationDelay: '250ms' }}>
            <StatCard title="Avg Orders/Day" value={avgOrdersPerDay.toFixed(1)} icon={<PackageIcon className="h-4 w-4" />} />
          </div>
        </div>

        {formError && <Alert variant="danger" description={formError} dismissible onDismiss={() => setFormError(null)} />}
        {priceExceedsTotal && !formError && <Alert variant="warning" description={`Combo price must be less than individual total (${formatPaise(individualTotal, currency)})`} />}

        {/* Basic Info */}
        <div className={cn(GLASS_FORM, 'p-5 space-y-4')}>
          <h2 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider">Basic Info</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label="Combo Name" required>
              <Input value={name} onChange={(val) => { setName(val); setFormError(null); }} placeholder="e.g. Lunch Combo" aria-label="Combo name" />
            </FormField>
            <FormField label={`Combo Price (${getCurrencySymbol(currency)})`} required>
              <Input value={priceDisplay} onChange={(val) => { setPriceDisplay(val); setFormError(null); }} type="number" placeholder="299.00" aria-label="Combo price" />
            </FormField>
          </div>
          <div className="flex items-center gap-3">
            <Toggle checked={isActive} onChange={setIsActive} aria-label="Active status" />
            <span className="text-sm text-neutral-600 dark:text-neutral-400">{isActive ? 'Active' : 'Inactive'}</span>
          </div>
        </div>

        {/* Combo Image */}
        <div className={cn(GLASS_FORM, 'p-5 space-y-4')}>
          <h2 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider flex items-center gap-2">
            <ImageIcon /> Combo Image
          </h2>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">Optional — displayed on the POS menu browser</p>
          <FileUpload
            onUpload={handleImageUpload}
            accept="image/png,image/jpeg,image/webp"
            maxSize={5 * 1024 * 1024}
            preview={imagePreview}
            variant="dropzone"
          />
        </div>

        {/* Items */}
        <div className={cn(GLASS, 'overflow-hidden')}>
          <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-100 dark:border-neutral-700/50">
            <h2 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider">Items ({items.length})</h2>
            <Button variant="secondary" size="sm" onClick={openPicker}><PlusIcon /><span className="ml-1.5">Add Item</span></Button>
          </div>
          {items.length === 0 ? (
            <div className="p-8 text-center"><p className="text-sm text-neutral-500 dark:text-neutral-400">Add at least 2 items</p></div>
          ) : (
            <div className="overflow-x-auto"><DataTable<SelectedItem> columns={itemColumns} data={items} /></div>
          )}
        </div>

        {/* Availability */}
        <div className={cn(GLASS_FORM, 'p-5 space-y-4')}>
          <h2 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider">Availability</h2>

          {/* Validity date window — seasonal combos run only between these dates.
              Independent of the time-of-day window below. Leave blank for no bound. */}
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Valid From">
              <DatePicker value={validFrom} onChange={setValidFrom} maxDate={validUntil} placeholder="No start date" />
            </FormField>
            <FormField label="Valid Until">
              <DatePicker value={validUntil} onChange={setValidUntil} minDate={validFrom} placeholder="No end date" />
            </FormField>
          </div>

          <div className="flex items-center gap-3 pt-2 border-t border-neutral-100 dark:border-neutral-700/50">
            <Toggle checked={alwaysAvailable} onChange={setAlwaysAvailable} aria-label="Always available" />
            <span className="text-sm text-neutral-600 dark:text-neutral-400">{alwaysAvailable ? 'Always available' : 'Time-based'}</span>
          </div>
          {!alwaysAvailable && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Start Time">
                  <TimePicker value={availStart} onChange={setAvailStart} />
                </FormField>
                <FormField label="End Time">
                  <TimePicker value={availEnd} onChange={setAvailEnd} />
                </FormField>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Days</label>
                <div className="flex flex-wrap gap-2" role="group" aria-label="Available days">
                  {DAY_LABELS.map((label, idx) => (
                    <button key={idx} type="button" onClick={() => toggleDay(idx)}
                      aria-pressed={availDays.includes(idx)}
                      className={cn('px-3 py-1.5 rounded-full text-xs font-bold transition-all', availDays.includes(idx) ? 'bg-violet-600 text-white shadow-md' : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300')}
                      aria-label={`Toggle ${label}`}>{label}</button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <Modal open={pickerOpen} onClose={() => setPickerOpen(false)} title="Add Item" variant="wide">
        <div className="space-y-4 p-1">
          <Input value={productSearch} onChange={handleProductSearch} placeholder="Search products..." aria-label="Search products" />
          <div className="max-h-64 overflow-y-auto space-y-1" role="listbox" aria-label="Product list">
            {filteredProducts.length === 0 ? (
              <p className="text-sm text-neutral-500 dark:text-neutral-400 text-center py-4">No products found</p>
            ) : filteredProducts.map((p) => (
              <button key={p.id} type="button" role="option" aria-selected={false} onClick={() => addProduct(p)}
                className="w-full flex items-center justify-between px-4 py-3 rounded-lg hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors text-left">
                <span className="text-sm font-semibold text-neutral-800 dark:text-neutral-200">{p.name}</span>
                <span className="text-xs font-mono tabular-nums text-neutral-500 dark:text-neutral-400">{formatPaise(p.price, currency)}</span>
              </button>
            ))}
          </div>
        </div>
      </Modal>

      <ConfirmDialog open={deleteConfirmOpen} title={`Delete "${name}"?`}
        description="This will deactivate the combo. It will no longer appear on the POS menu."
        confirmLabel="Delete Combo" variant="danger" loading={isDeleting}
        onConfirm={handleDelete} onCancel={() => setDeleteConfirmOpen(false)} />
    </PageContainer>
  );
}

export function ComboDetailPage() {
  return <ErrorBoundary><ComboDetailContent /></ErrorBoundary>;
}
